import { SIZES } from '@libs/ahoy/src';
import { ReactNode } from 'react';

import { IAlert } from './Alert';
import AlertType from './AlertType';
import BannerType from './BannerType';
import DialogType from './DialogType';
import { actionCreators } from './actions';

const closeToast = (id: number) => {
  window.top!.store.dispatch(actionCreators.closeToast(id));
};

const closeLoadingToast = (id: number) => {
  window.top!.store.dispatch(actionCreators.closeLoadingToast(id));
};

export interface Toast {
  id: number;
  text: string;
  timeout?: number;
  showCloseButton?: boolean;
  isProcessing?: boolean;
  action?: () => void;
  actionLabel?: string;
}

export const addToast = (
  text: string,
  timeout = 3000,
  showCloseButton = true,
  isProcessing = false,
  action?: () => void,
  actionLabel?: string,
) => {
  const addToastAction = (actionCreators.addToast as TSFixMe)(
    text,
    timeout,
    showCloseButton,
    isProcessing,
    action,
    actionLabel,
  );
  window.top!.store.dispatch(addToastAction);
  return {
    close: () => closeToast(addToastAction.payload.id),
  };
};

type Destructor = () => void;
export type PollingEffect<P> = (closeToast: () => void, params: P) => void | Destructor;

export interface LoadingToast<P extends Record<string, any> = {}> {
  id: number;
  text: string;
  pollResult: PollingEffect<P>;
  params: P;
}

export const addLoadingToast = <P>(text: string, pollingEffect: PollingEffect<P>, params?: P) => {
  const addLoadingToastAction = (actionCreators.addLoadingToast as TSFixMe)(text, pollingEffect, params ?? {});
  window.top!.store.dispatch(addLoadingToastAction);
  return {
    close: () => closeLoadingToast(addLoadingToastAction.payload.id),
  };
};

export const setToastsOffset = (offset: number) => {
  window.top!.store.dispatch((actionCreators.setToastsOffset as TSFixMe)(offset));
};

export interface Banner {
  id?: number;
  text: string;
  type: string;
  closable: boolean;
  action?: () => void;
  actionLabel?: string;
  onClose?: () => void;
}

export const addBanner = (
  text: string,
  type = 'error',
  closable = true,
  action?: () => void,
  actionLabel?: string,
  onClose?: () => void,
) => {
  window.top!.store.dispatch(actionCreators.addBanner({ text, type, closable, action, actionLabel, onClose }));
};

export const addErrorBanner = (
  text: string,
  closable = true,
  action?: () => void,
  actionLabel?: string,
  onClose?: () => void,
) => addBanner(text, BannerType.Error, closable, action, actionLabel, onClose);

export const addWarningBanner = (
  text: string,
  closable = true,
  action?: () => void,
  actionLabel?: string,
  onClose?: () => void,
) => addBanner(text, BannerType.Warning, closable, action, actionLabel, onClose);

export const addSuccessBanner = (
  text: string,
  closable = true,
  action?: () => void,
  actionLabel?: string,
  onClose?: () => void,
) => addBanner(text, BannerType.Success, closable, action, actionLabel, onClose);

export const addOngoingBanner = (
  text: string,
  closable = true,
  action?: () => void,
  actionLabel?: string,
  onClose?: () => void,
) => addBanner(text, BannerType.Ongoing, closable, action, actionLabel, onClose);

export const addNeutralBanner = (
  text: string,
  closable = true,
  action?: () => void,
  actionLabel?: string,
  onClose?: () => void,
) => addBanner(text, BannerType.Neutral, closable, action, actionLabel, onClose);

export const resetBanners = () => {
  window.top!.store.dispatch(actionCreators.resetBanners());
};

export const closeDialog = () => {
  window.top!.store.dispatch(actionCreators.closeDialog());
};

interface DialogAction {
  label: string;
  onClick: () => void;
}

export interface IDialog {
  title: string;
  text: string | ReactNode;
  primaryAction: DialogAction;
  secondaryAction: DialogAction | null;
  isClosable?: boolean;
  type?: DialogType;
  size?: Exclude<(typeof SIZES)[number], 'tiny' | 'smallest'>;
}

export const addDialog = (
  title: string,
  text: string | ReactNode,
  primaryAction: DialogAction = { label: 'Primary action', onClick: closeDialog },
  secondaryAction: DialogAction | null = null,
  isClosable = false,
  type = DialogType.Neutral,
  size?: Exclude<(typeof SIZES)[number], 'tiny' | 'smallest'>,
) => {
  window.top!.store.dispatch(
    actionCreators.addDialog({ title, text, primaryAction, secondaryAction, isClosable, type, size }),
  );
};

type AddTypeDialog = (
  title: string,
  text: string | ReactNode,
  primaryAction?: DialogAction,
  secondaryAction?: DialogAction,
  isClosable?: boolean,
  size?: Exclude<(typeof SIZES)[number], 'tiny' | 'smallest'>,
) => ReturnType<typeof addDialog>;

type AddTypeConfirmationDialog = (
  title: string,
  text: string | ReactNode,
  primaryAction?: DialogAction,
  secondaryAction?: DialogAction,
  type?: DialogType.Neutral | DialogType.Destructive,
  size?: Exclude<(typeof SIZES)[number], 'tiny' | 'smallest'>,
) => ReturnType<typeof addDialog>;

export const addErrorDialog: AddTypeDialog = (title, text, primaryAction, secondaryAction, isClosable, size) =>
  addDialog(title, text, primaryAction, secondaryAction, isClosable, DialogType.Error, size);
export const addWarningDialog: AddTypeDialog = (title, text, primaryAction, secondaryAction, isClosable, size) =>
  addDialog(title, text, primaryAction, secondaryAction, isClosable, DialogType.Warning, size);
export const addSuccessDialog: AddTypeDialog = (title, text, primaryAction, secondaryAction, isClosable, size) =>
  addDialog(title, text, primaryAction, secondaryAction, isClosable, DialogType.Success, size);
export const addConfirmationDialog: AddTypeConfirmationDialog = (
  title,
  text,
  primaryAction,
  secondaryAction,
  type = DialogType.Neutral,
  size,
) => addDialog(title, text, primaryAction, secondaryAction, true, type, size);

export const closeAlert = () => {
  window.top!.store.dispatch(actionCreators.closeAlert());
};

export const addAlert = (configuration: IAlert) => {
  window.top!.store.dispatch(actionCreators.addAlert(configuration));
};

type AddTypeAlert = (configuration: Omit<IAlert, 'type'>) => ReturnType<typeof addAlert>;
export const addErrorAlert: AddTypeAlert = (configuration) => addAlert({ type: AlertType.Error, ...configuration });
export const addConfirmAlert: AddTypeAlert = (configuration) => addAlert({ type: AlertType.Confirm, ...configuration });
export const addDestructiveAlert: AddTypeAlert = (configuration) =>
  addAlert({ type: AlertType.Destructive, ...configuration });
